"use client";

import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Badge,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  Avatar,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ShoppingCart,
  Receipt,
  ClearAll,
  Search,
  PersonAdd,
  Close,
  Person,
  Add,
  Check,
  Cancel,
  Delete,
  ArrowForward,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonText,
  IonToolbar,
} from "@ionic/react";
import { removeOutline, add as addIcon } from "ionicons/icons";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomer,
  searchCustomers,
} from "../../../api/customers/customers";
import {
  getBarInventoryByBranch,
  getBarInventoryByBranch_hotel,
  getCategories,
} from "../../../api/products/product";
import { createHotelBooking, fetchBookedDates } from "../../../api/orders/orders";
import { c } from "framer-motion/dist/types.d-Cjd591yU";

const StyledRoomCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  cursor: "pointer",
  transition: "transform 0.2s",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: theme.shadows[4],
  },
}));

const CalendarDay = styled(Paper)(({ theme, available, selected, booked, inCart, disabled }) => ({
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: (available && !booked && !inCart && !disabled) ? "pointer" : "default",
  backgroundColor: disabled
    ? theme.palette.grey[100]
    : selected 
    ? theme.palette.success.main 
    : inCart
    ? theme.palette.warning.main
    : booked 
    ? theme.palette.error.dark 
    : available 
    ? theme.palette.success.light 
    : theme.palette.grey[300],
  color: disabled 
    ? theme.palette.text.disabled
    : (selected || available || inCart) ? theme.palette.common.white : theme.palette.text.primary,
  "&:hover": {
    backgroundColor: (available && !booked && !inCart && !disabled) ? theme.palette.success.main : undefined,
  },
  opacity: (booked || inCart || disabled) ? 0.6 : 1,
}));

const HotelBookingPage = () => {
  const history = useHistory();
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  
  // Use local state for cart instead of CartContext
  const [cartItems, setCartItems] = useState([]);
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedCustomers, setSearchedCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  // Add near your other state declarations
const [discountPercentage, setDiscountPercentage] = useState(0);
const [discountAmount, setDiscountAmount] = useState(0);
  const [newCustomer, setNewCustomer] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [loading, setLoading] = useState({
    customers: false,
    rooms: false,
    availability: false,
  });
  const [processingBooking, setProcessingBooking] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [roomBookedDates, setRoomBookedDates] = useState({});

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  // NEW: Add state for payment processing dialog
  const [showProcessPaymentDialog, setShowProcessPaymentDialog] = useState(false);
  const [lastBookingCustomer, setLastBookingCustomer] = useState(null);

  // Calculate total amount from cart items
  const totalAmount = useMemo(() => {
    return cartItems.reduce((total, item) => total + (item.total_price || 0), 0);
  }, [cartItems]);

  // Cart management functions
  const addItem = (item) => {
    setCartItems(prevItems => {
      const newItems = [...prevItems, item];
      console.log("Added item to cart:", item);
      console.log("Cart items after addition:", newItems);
      return newItems;
    });
  };

  const removeItem = (id) => {
    setCartItems(prevItems => {
      const newItems = prevItems.filter(item => item.id !== id);
      console.log("Removed item with ID:", id);
      console.log("Cart items after removal:", newItems);
      return newItems;
    });
  };

  const clearCart = () => {
    setCartItems([]);
    console.log("Cart cleared");
  };

  // Get all dates that are already in cart for any room
  const getAllBookedDatesInCart = () => {
    const allDates = new Set();
    cartItems.forEach(item => {
      item.selected_dates.forEach(date => {
        allDates.add(date);
      });
    });
    return Array.from(allDates);
  };

  // Get dates in cart for a specific room
  const getCartDatesForRoom = (product_id) => {
    const roomItems = cartItems.filter(item => item.product_id === product_id);
    const dates = new Set();
    roomItems.forEach(item => {
      item.selected_dates.forEach(date => {
        dates.add(date);
      });
    });
    return Array.from(dates);
  };

  // Check if date is before today (disabled)
  // const isDateBeforeToday = (date) => {
  //   const today = new Date();
  //   today.setHours(0, 0, 0, 0);
  //   return date < today;
  // };

  // Room availability - UPDATED: Now uses actual API to get booked dates
  const getRoomBookedDates = async (product_id, month) => {
    setLoading(prev => ({ ...prev, availability: true }));
    
    console.log("Fetching booked dates for product_id:", product_id, "month:", month);
    try {
      // Use the actual API call to get booked dates
      const response = await fetchBookedDates(product_id, month);
      
      console.log("Booked dates responseee:", response);
      // Assuming the API returns an array of booked dates in 'YYYY-MM-DD' format
      const bookedDates = response.data || [];
      
      return bookedDates;
    } catch (error) {
      console.error("Error fetching room booked dates:", error);
      return [];
    } finally {
      setLoading(prev => ({ ...prev, availability: false }));
    }
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

 

  // UPDATED: Check if date is booked (in the booked dates array from API)
  const isDateBooked = (date, product_id) => {
    const dateKey = formatDateKey(date);
    const bookedDates = roomBookedDates[product_id];
    return bookedDates?.includes(dateKey);
  };

  const isDateInCart = (date, product_id) => {
    const dateKey = formatDateKey(date);
    const cartDates = getCartDatesForRoom(product_id);
    return cartDates.includes(dateKey);
  };

  const isDateSelected = (date) => {
    const dateKey = formatDateKey(date);
    return selectedDates.includes(dateKey);
  };
  const isDateBeforeToday = (date) => {
  const today = new Date();
  const todayKey = formatDateKey(today); // Format today's date the same way
  const dateKey = formatDateKey(date);
  return dateKey < todayKey; // Compare the string keys
};

  const isDateDisabled = (date) => {
    return isDateBeforeToday(date);
  };

  // UPDATED: A date is available if it's NOT booked AND not disabled AND not in cart
  const isDateAvailable = (date, product_id) => {
    return !isDateBooked(date, product_id) && !isDateDisabled(date) && !isDateInCart(date, product_id);
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) {
      return; // Don't allow selection of disabled dates (past dates)
    }

    const dateKey = formatDateKey(date);
    
    // UPDATED: Only allow selection if date is available
    if (!isDateAvailable(date, selectedRoom?.product_id)) {
      return; // Don't allow selection of booked dates, dates in cart, or past dates
    }

    setSelectedDates(prev => {
      if (prev.includes(dateKey)) {
        return prev.filter(d => d !== dateKey);
      } else {
        return [...prev, dateKey].sort();
      }
    });
  };

  const formatDateKey = (date) => {
  // Use local date parts to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`; // Returns YYYY-MM-DD in local time
};

  const navigateMonth = async (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);

    // Load booked dates for the new month when selected room is open
    if (selectedRoom && showBookingDialog) {
      const bookedDates = await getRoomBookedDates(selectedRoom.product_id, newMonth);
      setRoomBookedDates(prev => ({
        ...prev,
        [selectedRoom.product_id]: bookedDates
      }));
    }
  };

  const calculateDiscountedTotal = useMemo(() => {
  let discount = 0;
  if (discountPercentage > 0) {
    discount = (totalAmount * discountPercentage) / 100;
  } else if (discountAmount > 0) {
    discount = Math.min(discountAmount, totalAmount); // Ensure discount doesn't exceed total
  }
  // Round to 2 decimal places to avoid floating-point issues
  const finalTotal = Math.max(0, totalAmount - discount);
  return {
    discount: Math.round(discount * 100) / 100,
    finalTotal: Math.round(finalTotal * 100) / 100
  };
}, [totalAmount, discountPercentage, discountAmount]);

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    
    const calendar = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      calendar.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendar.push(date);
    }
    
    return calendar;
  };

  const calculateTotalPrice = () => {
    if (!selectedRoom || selectedDates.length === 0) return 0;
    return selectedRoom.price * selectedDates.length;
  };

  const handleRoomClick = async (room) => {
    setSelectedRoom(room);
    setSelectedDates([]);
    setCurrentMonth(new Date()); // Reset to current month when opening a room
    
    // Load booked dates for current month
    const bookedDates = await getRoomBookedDates(room.product_id, new Date());
    setRoomBookedDates(prev => ({
      ...prev,
      [room.product_id]: bookedDates
    }));
    
    setShowBookingDialog(true);
  };

  const handleAddBookingToCart = () => {
    if (selectedRoom && selectedDates.length > 0) {
      const bookingItem = {
        id: `${selectedRoom.product_id}-${selectedDates.join('-')}-${Date.now()}`,
        room_name: selectedRoom.name,
        product_id: selectedRoom.product_id,
        selected_dates: [...selectedDates],
        price_per_night: selectedRoom.price,
        total_price: calculateTotalPrice(),
        image: selectedRoom.img_src,
        room_type: selectedRoom.type,
        nights: selectedDates.length,
      };

      addItem(bookingItem);
      setShowBookingDialog(false);
      setSelectedRoom(null);
      setSelectedDates([]);
      
      setSnackbarMessage(`Added ${selectedRoom.name} booking for ${selectedDates.length} nights`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading((prev) => ({ ...prev, rooms: true }));
      const [roomsRes, categoriesRes] = await Promise.all([
        getBarInventoryByBranch_hotel(),
        getCategories(),
      ]);

      const roomsWithCategory = roomsRes.data.map(room => {
        const category = categoriesRes.data.find(c => c.category_id === room.roomCategory);
        return { ...room, category: category ? category.name : 'Unknown' };
      });

      setAllRooms(roomsWithCategory);
      setRooms(roomsWithCategory);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setSnackbarMessage("Failed to load rooms data.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading((prev) => ({ ...prev, rooms: false }));
    }
  };

  const fetchCustomers = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSearchedCustomers([]);
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await searchCustomers(searchTerm);
      setSearchedCustomers(response.data);
    } catch (error) {
      console.error("Error searching customers:", error);
      setSnackbarMessage("Failed to search customers.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (customerSearchQuery.trim() === '') {
      setSearchedCustomers([]);
      return;
    }
    const timerId = setTimeout(() => {
      fetchCustomers(customerSearchQuery);
    }, 500);
    return () => clearTimeout(timerId);
  }, [customerSearchQuery]);

  const handleBookingConfirmation = () => {
    setShowConfirmationDialog(true);
  };

  const onHandleCreateBooking = async () => {
    setProcessingBooking(true);
    
    const bookingData = {
      customer_id: selectedCustomer?.user_id || null,
      bookings: cartItems.map(item => ({
        product_id: item.product_id,
        selected_dates: item.selected_dates,
        total_price: calculateDiscountedTotal.finalTotal,

        nights: item.nights,
      })),
         discount: calculateDiscountedTotal.discount > 0 ? {
      amount: calculateDiscountedTotal.discount,
      type: discountPercentage > 0 ? 'percentage' : 'fixed',
      value: discountPercentage > 0 ? discountPercentage : discountAmount
    } : null, // Add discount details
      totalPrice:    calculateDiscountedTotal.finalTotal,

      customer_info: selectedCustomer,
    };

    console.log(bookingData)

    try {
      const res = await createHotelBooking(bookingData);
      console.log({res})

      if (res.status === true) {
        setSnackbarMessage("Booking created successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        // NEW: Set the last booking customer and show payment dialog
        setLastBookingCustomer(selectedCustomer);
        setShowProcessPaymentDialog(true);
        
        clearCart();
        setDiscountAmount(0)
        setDiscountPercentage(0)
        fetchInitialData();
        setSelectedCustomer(null);
        setShowConfirmationDialog(false);
      } else {
        setSnackbarMessage("Failed to create booking." + (res.message || ""));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.log({error})
      console.error("Error creating booking:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to create booking. Please try again.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setProcessingBooking(false);
    }
  };

  const handleAddCustomer = async () => {
    setLoading((prev) => ({ ...prev, customers: true }));
    try {
      const response = await createCustomer(newCustomer);
      setSelectedCustomer(response.data);
      setShowCustomerDialog(false);
      setNewCustomer({
        firstName: "",
        middleName: "",
        lastName: "",
        phone: "",
        email: "",
      });
      setCustomerSearchQuery("");
      setSnackbarMessage("Customer added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error creating customer:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to add customer. Please check inputs.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  // Enhanced remove item handler
  const cartItemRemoveHandler = (id) => {
    console.log("Attempting to remove item with ID:", id);
    console.log("Current cart items before removal:", cartItems);
    
    if (!id) {
      console.error("No ID provided for removal");
      return;
    }

    removeItem(id);
    
    setSnackbarMessage("Booking item removed from cart");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  const handleRemoveFromConfirmation = (itemId) => {
    console.log("Removing from confirmation dialog, ID:", itemId);
    cartItemRemoveHandler(itemId);
  };

  const handleClearCart = () => {
    clearCart();
    setSnackbarMessage("Cart cleared");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const filteredRooms = useMemo(() => {
    if (!Array.isArray(rooms)) {
      return [];
    }
    return rooms.filter((room) => {
      const matchesCategory = selectedCategory
        ? String(room.roomCategory) === String(selectedCategory)
        : true;
      const matchesSearch = room.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [rooms, selectedCategory, searchQuery]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar></IonToolbar>
      </IonHeader>
      <IonContent>
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#f5f7fa",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: "#667eea",
              color: "white",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Hotel Bookings and Reservations Dashboard
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {selectedCustomer ? (
                  <Chip
                    label={`${selectedCustomer.firstName} ${selectedCustomer.lastName} (${selectedCustomer.phone})`}
                    onDelete={() => {
                      setSelectedCustomer(null);
                      handleClearCart();
                    }}
                    deleteIcon={<Close />}
                    avatar={
                      <Avatar sx={{ bgcolor: "white", color: "#667eea" }}>
                        <Person />
                      </Avatar>
                    }
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "& .MuiChip-deleteIcon": { color: "white" },
                    }}
                  />
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => setShowCustomerDialog(true)}
                    sx={{
                      bgcolor: "white",
                      color: "#667eea",
                      "&:hover": { bgcolor: "#f0f0f0" },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Select Customer
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {/* Room Selection Panel */}
            <Box
              sx={{
                width: { xs: "100%", md: "70%" },
                p: 2,
                overflowY: "auto",
                borderRight: { md: "1px solid #e0e0e0" },
              }}
            >
              {/* Search and Categories */}
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search rooms..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
              </Box>
              
              <Tabs
                value={selectedCategory}
                onChange={(e, newValue) => {
                  setSelectedCategory(newValue);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="All Rooms" value={null} />
                {categories?.map((category, i) => (
                  <Tab
                    key={i}
                    label={category.name}
                    value={category.category_id}
                  />
                ))}
              </Tabs>

              {/* Room Grid */}
              {loading.rooms ? (
                <Grid container spacing={2}>
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <StyledRoomCard
                        sx={{
                          height: 200,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="subtitle1">
                          Loading...
                        </Typography>
                      </StyledRoomCard>
                    </Grid>
                  ))}
                </Grid>
              ) : filteredRooms?.length > 0 ? (
                <Grid container spacing={2}>
                  {filteredRooms.map((room, i) => (
                    <Grid item xs={6} sm={4} md={3} key={i}>
                      <StyledRoomCard onClick={() => handleRoomClick(room)}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Avatar
                            src={
                              room.img_src ||
                              "/placeholder.svg?height=80&width=80&query=room"
                            }
                            alt={room.name}
                            sx={{ width: 80, height: 80, mb: 1 }}
                          />
                          <Typography
                            variant="subtitle1"
                            align="center"
                            sx={{ fontWeight: 600 }}
                          >
                            {room.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                          >
                            {room.type}
                          </Typography>
                          <Typography
                            variant="h6"
                            color="primary"
                            sx={{ mt: 1, fontWeight: 600 }}
                          >
                            {formatCurrencyN(room.price)}/night
                          </Typography>
                          <Chip
                            label={`Capacity: ${room.capacity || 2} people`}
                            size="small"
                            color="secondary"
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      </StyledRoomCard>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", p: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No rooms found
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Cart Panel */}
            <Box
              sx={{
                width: { xs: "100%", md: "30%" },
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                boxShadow: { xs: "0 -4px 12px rgba(0,0,0,0.1)", md: "none" },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  <Badge
                    badgeContent={cartItems.length}
                    color="primary"
                    sx={{ mr: 1 }}
                  >
                    <ShoppingCart />
                  </Badge>
                  Current Booking
                </Typography>
                <Button
                  size="small"
                  color="error"
                  startIcon={<ClearAll />}
                  onClick={handleClearCart}
                  disabled={cartItems.length === 0}
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  Clear
                </Button>
              </Box>
              
              <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                {cartItems.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                      py: 4,
                    }}
                  >
                    <ShoppingCart
                      sx={{ fontSize: 64, color: "text.disabled", mb: 1 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      Your booking cart is empty
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select rooms to add to booking
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {cartItems.map((item, i) => (
                      <ListItem
                        key={`${item.id}-${i}`}
                        sx={{
                          py: 1,
                          flexDirection: "column",
                          alignItems: "stretch",
                          borderBottom: "1px dashed #eee",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            width: "100%",
                          }}
                        >
                          <Avatar
                            src={
                              item?.image ||
                              "/placeholder.svg?height=40&width=40&query=room"
                            }
                            alt={item.room_name}
                            sx={{ width: 40, height: 40, mr: 2 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600 }}
                            >
                              {item.room_name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {item.nights} night(s)
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "primary.main" }}
                            >
                              {formatCurrencyN(item.total_price)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Dates: {item.selected_dates.join(', ')}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => cartItemRemoveHandler(item.id)}
                          >
                            <Close />
                          </IconButton>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {/* Booking Summary */}
              {/* <Box
                sx={{
                  p: 2,
                  borderTop: "1px solid #e0e0e0",
                  bgcolor: "background.paper",
                  boxShadow: "0 -2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Total:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#667eea" }}
                  >
                    {formatCurrencyN(totalAmount)}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleBookingConfirmation}
                  startIcon={<Receipt />}
                  sx={{
                    mt: 2,
                    bgcolor: "#667eea",
                    color: "white",
                    "&:hover": { bgcolor: "#5a6fd8" },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1.5,
                  }}
                  disabled={
                    cartItems.length === 0 || selectedCustomer === null
                  }
                >
                  Confirm Booking
                </Button>
              </Box> */}

              {/* Booking Summary - UPDATED WITH DISCOUNT */}
<Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: 'background.paper', boxShadow: '0 -2px 8px rgba(0,0,0,0.05)' }}>

  {/* Discount Input Section */}
  <Box sx={{ mb: 2, p: 2, border: '1px dashed #e0e0e0', borderRadius: 1 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Apply Discount</Typography>
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={6}>
        <TextField
          size="small"
          fullWidth
          label="Percentage %"
          type="number"
          InputProps={{ inputProps: { min: 0, max: 100 } }}
          value={discountPercentage}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            setDiscountPercentage(value);
            if (value > 0) setDiscountAmount(0); // Clear amount if using percentage
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" align="center" color="text.secondary">OR</Typography>
      </Grid>
      <Grid item xs={6}>
        <TextField
          size="small"
          fullWidth
          label="Fixed Amount"
          type="number"
          InputProps={{ inputProps: { min: 0 } }}
          value={discountAmount}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            setDiscountAmount(value);
            if (value > 0) setDiscountPercentage(0); // Clear percentage if using amount
          }}
        />
      </Grid>
    </Grid>
  </Box>

  {/* Total Breakdown */}
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2">Subtotal:</Typography>
      <Typography variant="body2">{formatCurrencyN(totalAmount)}</Typography>
    </Box>
    {calculateDiscountedTotal.discount > 0 && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="error.main">Discount:</Typography>
        <Typography variant="body2" color="error.main">-{formatCurrencyN(calculateDiscountedTotal.discount)}</Typography>
      </Box>
    )}
    <Divider sx={{ my: 1 }} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Total:</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#667eea' }}>
        {formatCurrencyN(calculateDiscountedTotal.finalTotal)}
      </Typography>
    </Box>
  </Box>

  <Button
    fullWidth
    variant="contained"
    size="large"
    onClick={handleBookingConfirmation}
    startIcon={<Receipt />}
    sx={{
      bgcolor: '#667eea',
      color: 'white',
      '&:hover': { bgcolor: '#5a6fd8' },
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      py: 1.5,
    }}
    disabled={cartItems.length === 0 || selectedCustomer === null}
  >
    Confirm Booking
  </Button>
</Box>
            </Box>
          </Box>

          {/* Room Booking Dialog */}
          <Dialog
            open={showBookingDialog}
            onClose={() => setShowBookingDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>
              Book Room
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowBookingDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {selectedRoom && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar
                      src={
                        selectedRoom.img_src ||
                        "/placeholder.svg?height=60&width=60&query=room"
                      }
                      alt={selectedRoom.name}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        {selectedRoom.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {selectedRoom.type}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                        {formatCurrencyN(selectedRoom.price)}/night
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Availability Calendar */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem', textAlign: 'center' }}>
                    Select Available Dates
                  </Typography>
                  
                  {loading.availability && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Loading availability...
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {/* Calendar Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Button 
                        onClick={() => navigateMonth(-1)}
                        size="small"
                        sx={{ minWidth: 30, width: 30, height: 30 }}
                      >
                        ‹
                      </Button>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                      </Typography>
                      <Button 
                        onClick={() => navigateMonth(1)}
                        size="small"
                        sx={{ minWidth: 30, width: 30, height: 30 }}
                      >
                        ›
                      </Button>
                    </Box>

                    {/* Day Headers - Fixed 7 columns */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                        <Box 
                          key={day} 
                          sx={{ 
                            textAlign: 'center',
                            py: 0.5
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                            {day}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Calendar Days - Fixed 7 columns */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                      {generateCalendar().map((date, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
                          {date ? (
                            <CalendarDay
                              // UPDATED: available is now based on NOT booked, NOT disabled, and NOT in cart
                              available={isDateAvailable(date, selectedRoom.product_id)}
                              booked={isDateBooked(date, selectedRoom.product_id)}
                              inCart={isDateInCart(date, selectedRoom.product_id)}
                              disabled={isDateDisabled(date)}
                              selected={isDateSelected(date)}
                              onClick={() => handleDateClick(date)}
                              sx={{
                                width: 28,
                                height: 28,
                                minWidth: 28,
                                fontSize: '0.7rem',
                                borderRadius: 1
                              }}
                            >
                              {date.getDate()}
                            </CalendarDay>
                          ) : (
                            <Box sx={{ width: 28, height: 28 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Compact Legend */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: 'success.light', borderRadius: '1px' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Available</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: 'success.main', borderRadius: '1px' }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Selected</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: 'warning.main', borderRadius: '1px', opacity: 0.6 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>In Cart</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: 'error.dark', borderRadius: '1px', opacity: 0.6 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Booked</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, backgroundColor: 'grey.100', borderRadius: '1px', opacity: 0.6 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Past Date</Typography>
                    </Box>
                  </Box>

                  {/* Booking Summary */}
                  {selectedDates.length > 0 && (
                    <Alert severity="info" sx={{ mb: 1, py: 1 }}>
                      <Box sx={{ fontSize: '0.8rem' }}>
                        <div><strong>Selected:</strong> {selectedDates.length} night(s)</div>
                        <div><strong>Dates:</strong> {selectedDates.join(', ')}</div>
                        <div><strong>Total:</strong> {formatCurrencyN(calculateTotalPrice())}</div>
                      </Box>
                    </Alert>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setShowBookingDialog(false)}
                size="small"
                sx={{ borderRadius: 1, textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddBookingToCart}
                disabled={selectedDates.length === 0}
                startIcon={<Add />}
                sx={{
                  bgcolor: "#667eea",
                  color: "white",
                  "&:hover": { bgcolor: "#5a6fd8" },
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Add ({selectedDates.length} nights)
              </Button>
            </DialogActions>
          </Dialog>

          {/* Booking Confirmation Dialog */}
          <Dialog
            open={showConfirmationDialog}
            onClose={() => setShowConfirmationDialog(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Check sx={{ color: 'success.main' }} />
              Confirm Booking Details
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowConfirmationDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* Customer Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                  Customer Information
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {selectedCustomer?.firstName} {selectedCustomer?.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedCustomer?.phone} • {selectedCustomer?.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Booking Summary */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Booking/Reservation Summary
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Room</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nights</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Price/Night</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cartItems.map((item, index) => (
                      <TableRow key={`${item.id}-${index}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              src={item.image}
                              alt={item.room_name}
                              sx={{ width: 40, height: 40 }}
                            />
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {item.room_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.room_type}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.selected_dates.join(', ')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.nights}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrencyN(item.price_per_night)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrencyN(item.total_price)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Remove this booking">
                            <IconButton 
                              color="error" 
                              onClick={() => handleRemoveFromConfirmation(item.id)}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
              {/* In the Booking Confirmation Dialog - Update Total Amount Section */}
<TableRow>
  <TableCell colSpan={4} align="right" sx={{ border: 'none', pt: 2 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Subtotal:
      </Typography>
      {calculateDiscountedTotal.discount > 0 && (
        <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
          Discount:
        </Typography>
      )}
      <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
        Total Amount:
      </Typography>
    </Box>
  </TableCell>
  <TableCell align="right" sx={{ border: 'none', pt: 2 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {formatCurrencyN(totalAmount)}
      </Typography>
      {calculateDiscountedTotal.discount > 0 && (
        <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
          -{formatCurrencyN(calculateDiscountedTotal.discount)}
        </Typography>
      )}
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mt: 1 }}>
        {formatCurrencyN(calculateDiscountedTotal.finalTotal)}
      </Typography>
    </Box>
  </TableCell>
  <TableCell sx={{ border: 'none' }}></TableCell>
</TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Additional Information */}
              {cartItems.length === 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Your cart is empty. Please add some bookings before proceeding.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Please review all booking details carefully before proceeding. 
                    Once confirmed, this booking will be processed and the rooms will be reserved for the selected dates.
                  </Typography>
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button
                onClick={() => setShowConfirmationDialog(false)}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: "none", 
                  fontWeight: 600,
                  px: 3
                }}
                startIcon={<Cancel />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onHandleCreateBooking}
                disabled={processingBooking || cartItems.length === 0}
                startIcon={
                  processingBooking ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Check />
                  )
                }
                sx={{
                  bgcolor: "success.main",
                  color: "white",
                  "&:hover": { bgcolor: "success.dark" },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3
                }}
              >
                {processingBooking ? "Processing..." : "Proceed with Booking/Reservation"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* NEW: Process Payment Dialog */}
          <Dialog open={showProcessPaymentDialog} onClose={() => setShowProcessPaymentDialog(false)}>
            <DialogTitle>Process Payment</DialogTitle>
            <DialogContent>
                <Typography>Do you want to process payment for this customer?</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowProcessPaymentDialog(false)}>No</Button>
                <Button 
                    onClick={() => {
                        history.push(`/folder/raise-receipt/AddReceipt`, { 
                          customer: lastBookingCustomer,
                          bookingData: {
                            totalAmount: totalAmount,
                            type: 'hotel_booking'
                          }
                        });
                        setShowProcessPaymentDialog(false);
                    }}
                    startIcon={<ArrowForward />}
                >
                    Yes, Proceed
                </Button>
            </DialogActions>
          </Dialog>

          {/* Customer Selection Dialog */}
          <Dialog
            open={showCustomerDialog}
            onClose={() => setShowCustomerDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pb: 1 }}>
              {selectedCustomer ? "Change Customer" : "Select Customer"}
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowCustomerDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Search customers by name or phone..."
                  variant="outlined"
                  size="small"
                  value={customerSearchQuery}
                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {loading.customers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : searchedCustomers.length > 0 ? (
                <List>
                  {searchedCustomers?.map(customer => (
                    <ListItem
                      button
                      key={customer.user_id}
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowCustomerDialog(false);
                        setCustomerSearchQuery('');
                      }}
                    >
                      <ListItemText  
                        primary={`${customer.firstName} ${customer.lastName}`}
                        secondary={`${customer.phone}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : customerSearchQuery ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography>No customers found matching "{customerSearchQuery}"</Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography>Enter a name or phone number to search for customers</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Add New Customer
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={newCustomer.firstName}
                    onChange={(e) =>
                      setNewCustomer({
                        ...newCustomer,
                        firstName: e.target.value,
                      })
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={newCustomer.phone}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, phone: e.target.value })
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={newCustomer.email}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, email: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={() => setShowCustomerDialog(false)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddCustomer}
                disabled={!newCustomer.firstName || !newCustomer.phone}
                startIcon={<Add />}
                sx={{
                  bgcolor: "#667eea",
                  color: "white",
                  "&:hover": { bgcolor: "#5a6fd8" },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Add Customer
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default HotelBookingPage;